/**
 * ScenarioParamAddPicker.js
 *
 * "조정만 보기" 뷰에서 아직 손대지 않은 파라미터를 목록에 끌어오는 추가 버튼.
 *
 * 적용 단위(거점/분류) 추가 버튼과 생김새는 같지만 동작이 다르다 —
 * 적용 단위는 "일단 추가하고 나서 대상을 고르는" 흐름이지만, 파라미터는 무엇을 추가할지
 * 먼저 정해야 의미가 있으므로 버튼을 누르면 후보 메뉴를 열고 고른 뒤에 행이 생긴다.
 *
 * 고른 파라미터는 아직 값이 없어 "조정됨" 판정에 안 걸리므로, 호출측이 pin(고정) 집합에
 * 넣어 조정만 뷰에서도 계속 보이게 해야 한다. 안 그러면 추가하자마자 사라진다.
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Menu, MenuItem, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { FONT } from "@plannel/components/ai/typographyTokens";
import { C } from "@plannel/components/ai/aiTokens";
import { PARAM_CARD_RADIUS } from "./scenarioParamUtil";

// items: [{ key, label }] — 이미 보이는 파라미터를 제외한 후보. 비면 버튼이 비활성.
export default function ScenarioParamAddPicker({ items, onPick, accent, disabled }) {
  const { t } = useTranslation();
  const [anchor, setAnchor] = useState(null);
  const empty = !items?.length;
  const off = disabled || empty;
  return (
    <>
      <Box component="button" type="button" disabled={off}
        onClick={(e) => setAnchor(e.currentTarget)}
        title={empty ? t("allParamsAddedLabel") : undefined}
        sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.4,
              width: "100%", border: `1px dashed ${accent}70`, borderRadius: PARAM_CARD_RADIUS,
              bgcolor: "transparent", color: accent, fontSize: FONT.BODY, fontWeight: 700,
              fontFamily: "inherit", p: "6px 8px",
              opacity: off ? 0.45 : 1, cursor: off ? "not-allowed" : "pointer",
              transition: "background-color .12s, border-color .12s",
              "&:hover": off ? {} : { bgcolor: `${accent}0E`, borderColor: accent } }}>
        <AddIcon sx={{ fontSize: 13 }} />
        {empty ? t("allParamsAddedLabel") : t("addParamLabel")}
      </Box>
      <Menu anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{ paper: { sx: { maxHeight: 280, borderRadius: PARAM_CARD_RADIUS } } }}>
        {(items || []).map(it => (
          <MenuItem key={it.key} dense
            onClick={() => { setAnchor(null); onPick?.(it.key); }}
            sx={{ py: 0.5 }}>
            <Typography sx={{ fontSize: FONT.BODY, color: C.text1 }}>{it.label}</Typography>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
